import papa, {ParseError, ParseResult} from "papaparse";
import {Hint, InputType, PromptType, QuestionDefinition} from "../state/questions";
import {BOT_DIVIDER, HintDuration} from "../state/state";
import {phraseUniqueLetters} from "./utils";
import {isUndefined} from "engage-common/utils";

enum SpecialOptions {
	No = "no",
	Poll = "poll",
	DontScrambleLast = "don't scramble last",
	DoNotScrambleLast = "do not scramble last",
	RightAnswerLast = "right answer last"
}

enum TriviaFields {
	PromptType,
	Prompt,
	Explanation,
	Sources,
	Special,
	InputType,
	Answer,
	Extra //Could be another answer or the number input ratio, not required for phrase.
}

export enum IssueType {
	Error,
	Warning
}

export type TriviaIssue = {
	issueType: IssueType
	row: number
	question: number
	message: string
}

export type TriviaFileIssues = {
	[key in IssueType] : TriviaIssue[]
}

const RequiredFields = [TriviaFields.PromptType, TriviaFields.Prompt, TriviaFields.Special,
	TriviaFields.InputType, TriviaFields.Answer];

export type TriviaLoad = {questions?: QuestionDefinition[], issues:TriviaFileIssues};

export function loadTriviaFromFile(file:any):Promise<TriviaLoad> {
	return new Promise<TriviaLoad>(resolve => {
		const issues:TriviaFileIssues = {
			[IssueType.Error]: [],
			[IssueType.Warning]: []
		};
		papa.parse(file, {
			error(error: ParseError): void {
				issues[IssueType.Error].push({
					issueType: IssueType.Error,
					message: error.message,
					row: 0,
					question: 0
				});
				resolve({issues});
			},
			//TODO: Gives this a ParseResult type?
			complete(results: ParseResult<any>): void {
				let questions:QuestionDefinition[] = [];
				let questionIndex = 0;
				//Trim any empty lines at the end of the file/results
				for(let x = results.data.length - 1; x > 0; x--) {
					const row = results.data[x];
					if(row.length === 0 || (row.length === 1 && row[0].length === 0)) {
						results.data.splice(-1, 1);
					} else {
						break;
					}
				}
				for(let i = 1; i < results.data.length; i++) {
					const row = results.data[i];
					let hintRows = [];
					let j = 0;
					if(row[TriviaFields.PromptType] === PromptType.Hint) {
						for(j = i+1; j < results.data.length; j++) {
							const hintRow = results.data[j];
							if(hintRow[0] === "") {
								hintRows.push(hintRow);
							} else {
								break;
							}
						}
					}
					let q = parseQuestion(row, i+1, questionIndex++, issues, hintRows);

					//If we have hintRows, lets update the question row number after we parse questions so
					// we can use row number in the upload issues logs.
					if(j > 0) {
						i = j - 1;
					}
					if(q) {
						questions.push(q);
					}
				}
				resolve({questions, issues});
			}
		});
	});
}

function parseQuestion(row:string[], qRowNumber:number, questionIndex:number, issues:TriviaFileIssues, hintRows?:string[][]):QuestionDefinition | undefined {
	if(!commonErrorChecks(row,qRowNumber,questionIndex,issues)) {
		return undefined;
	}

	let hints:(Hint|string)[]|undefined = [];
	let promptOptions:{promptType:PromptType.Text} | {promptType:PromptType.Hint, hints:(Hint|string)[]};
	let duration:number|undefined;
	if(row[TriviaFields.PromptType] === PromptType.Hint) {
		if(hintRows) {
			if(hintRows.length < 2){
				issues[IssueType.Error].push({
					issueType: IssueType.Error,
					message: "Requires at least 2 hints.",
					row: qRowNumber,
					question: questionIndex+1
				});
				return undefined;
			}
			for(let i = 0; i < hintRows.length; i++) {
				const hintRow = hintRows[i];
				if(hintRow[1].length && hintRow[2].length) {
					hints.push({
						prompt: hintRow[1],
						answer: hintRow[2]
					});
				} else if (hintRow[1].length) {
					hints.push(hintRow[1]);
				} else {
					issues[IssueType.Error].push({
						issueType: IssueType.Error,
						message: `Hint number '${i+1}' does not have required field.`,
						row: qRowNumber + i + 1,
						question: questionIndex+1
					});
				}
			}
			promptOptions = {
				promptType: PromptType.Hint,
				hints
			};
			duration = hints.length * HintDuration;
		} else {
			issues[IssueType.Error].push({
				issueType: IssueType.Error,
				message: "Requires at least 2 hints.",
				row: qRowNumber,
				question: questionIndex+1
			});
			return undefined;
		}
	} else if(row[TriviaFields.PromptType] === PromptType.Text) {
		promptOptions = {
			promptType: PromptType.Text
		};
	} else {
		issues[IssueType.Error].push({
			issueType: IssueType.Error,
			message: "Incorrect Prompt Type. Should be 'Text' or 'Hint'.",
			row: qRowNumber,
			question: questionIndex+1
		});
		return undefined;
	}

	const specialOptionLC = row[TriviaFields.Special].toLowerCase();

	let question:Pick<QuestionDefinition, "index"|"prompt"|"explanation"|"sources"|"poll"> = {
		index: questionIndex,
		prompt: row[TriviaFields.Prompt],
		explanation: row[TriviaFields.Explanation],
		sources: row[TriviaFields.Sources],
		poll: specialOptionLC === SpecialOptions.Poll || specialOptionLC === "true"
	};

	if(row[TriviaFields.InputType] === InputType.MC) {
		let answer = row[TriviaFields.Answer];
		let otherChoices = row.slice(TriviaFields.Extra).filter(val => val.length !== 0);
		const dontScrambleLast = specialOptionLC === SpecialOptions.DontScrambleLast || specialOptionLC === SpecialOptions.DoNotScrambleLast;
		const rightAnswerLast = specialOptionLC === SpecialOptions.RightAnswerLast;
		if(rightAnswerLast) {
			otherChoices.push(answer);
		} else {
			otherChoices.unshift(answer);
		}
		shuffleArray(otherChoices, dontScrambleLast || rightAnswerLast);
		return {
			...question,
			...promptOptions,
			inputType: InputType.MC,
			correctIndex: otherChoices.indexOf(answer),
			options: otherChoices,
			duration: duration ?? 20*1000 / BOT_DIVIDER,
			multiSubmit: false,
		};
	} else if(row[TriviaFields.InputType] === InputType.Number) {
		let answer = Number(row[TriviaFields.Answer]);
		if(isNaN(answer)) {
			issues[IssueType.Error].push({
				issueType: IssueType.Error,
				message: "Answer needs to be a number.",
				row: qRowNumber,
				question: questionIndex+1
			});
			return undefined;
		}
		let ratio = Number(row[TriviaFields.Extra]);
		if(isNaN(ratio)) {
			ratio = 1;
		}
		return {
			...question,
			...promptOptions,
			inputType: InputType.Number,
			correctNumber: answer,
			maxDifference: ratio,
			duration: duration ?? 20*1000 / BOT_DIVIDER,
			multiSubmit: false,
		};
	} else if(row[TriviaFields.InputType] === InputType.Sort) {
		let sortedChoices = row.slice(TriviaFields.Answer).filter(val => val.length !== 0);
		let shuffledChoices = row.slice(TriviaFields.Answer).filter(val => val.length !== 0);
		shuffleArray(shuffledChoices);
		return {
			...question,
			...promptOptions,
			inputType: InputType.Sort,
			correctIndices: sortedChoices.map(value => shuffledChoices.indexOf(value)),
			options: shuffledChoices,
			duration: duration ?? 30*1000 / BOT_DIVIDER,
			multiSubmit: false,
		};
	} else if(row[TriviaFields.InputType] === InputType.Phrase) {
		let phrase = row[TriviaFields.Answer].toUpperCase();
		let shuffledLetters = phraseUniqueLetters(phrase);
		shuffleArray(shuffledLetters);
		let hintTime:number[] = [];
		let oneThird = shuffledLetters.length / 3;

		//Two seconds for the first third
		for(let i = 0; i < Math.round(oneThird); i++) {
			hintTime.push(i);
			hintTime.push(i);
			hintTime.push(i);
		}
		//Three seconds for the second third
		for(let i = Math.round(oneThird); i < Math.round(oneThird * 2); i++) {
			hintTime.push(i);
			hintTime.push(i);
			hintTime.push(i);
			hintTime.push(i);
		}
		//Four seconds for the last third
		for(let i = Math.round(oneThird*2); i < shuffledLetters.length; i++) {
			hintTime.push(i);
			hintTime.push(i);
			hintTime.push(i);
			hintTime.push(i);
			hintTime.push(i);
		}

		return {
			...question,
			...promptOptions,
			phrase,
			shuffledLetters,
			inputType: InputType.Phrase,
			duration: shuffledLetters.length * 4 * 1000,
			multiSubmit: true,
			hintTime
		};
	} else {
		issues[IssueType.Error].push({
			issueType: IssueType.Error,
			message: "Incorrect Input Type. See wiki for options.",
			row: qRowNumber,
			question: questionIndex+1
		});
		return undefined;
	}
}

function commonErrorChecks(row:string[], rowNumber:number, questionIndex:number, issues:TriviaFileIssues) : boolean {
	if(row.length < TriviaFields.Extra) {
		issues[IssueType.Error].push({
			issueType: IssueType.Error,
			message: "Incorrect number of fields.",
			row: rowNumber,
			question: questionIndex+1
		});
		return false;
	}
	let valid = true;
	for(let item in TriviaFields) {
		if(TriviaFields.hasOwnProperty(item)) {
			let value = Number(item);
			if(!isNaN(value) && (isUndefined(row[value]) || row[value].length === 0) && value !== TriviaFields.Sources) {
				//Extra is a required field for any Input type outside Phrase
				if(RequiredFields.includes(value) || (row[TriviaFields.InputType] !== InputType.Phrase && value === TriviaFields.Extra)) {
					issues[IssueType.Error].push({
						issueType: IssueType.Error,
						message: `Missing required field '${TriviaFields[value]}'.`,
						row: rowNumber,
						question: questionIndex+1
					});
					valid = false;
				} else if(!(row[TriviaFields.InputType] === InputType.Phrase && value === TriviaFields.Extra)) {
					issues[IssueType.Warning].push({
						issueType: IssueType.Warning,
						message: `Missing optional field '${TriviaFields[value]}'.`,
						row: rowNumber,
						question: questionIndex+1
					});
				}
			}
			if(value === TriviaFields.Special) {
				const option = row[value].toLowerCase();
				if(option && option !== "true" && option !== "false" && !Object.values(SpecialOptions).some(v => v === option)) {
					issues[IssueType.Warning].push({
						issueType: IssueType.Warning,
						message: "Special option invalid. See wiki for options.",
						row: rowNumber,
						question: questionIndex+1
					});
				}
			}
		}
	}
	return valid;
}

/**
 * Randomize array element order in-place.
 * Using Durstenfeld shuffle algorithm.
 * https://stackoverflow.com/a/12646864/413443
 */
function shuffleArray(array:any[], leaveLast:boolean = false) {
	for (let i = array.length - (leaveLast ? 2 : 1); i > 0; i--) {
		let j = Math.floor(Math.random() * (i + 1));
		let temp = array[i];
		array[i] = array[j];
		array[j] = temp;
	}
}